import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { CSRF_HEADER_NAME, createBffBaseQuery } from './csrf';
import {
  API_CLIENT_TYPES,
  assertApiClientAllowlisted
} from './apiClientAllowlist';
import { shouldBlockAccountRequest } from './accountRequestPolicy';

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

const requestWithoutBrowserCredentials = (args) => {
  const requestArgs = typeof args === 'string' ? { url: args } : { ...args };
  const headers = new Headers(requestArgs.headers);

  // Public and external clients must not inherit the BFF's CSRF contract.
  // Provider-specific headers such as Monobank's X-Token remain explicit at
  // the endpoint that owns that provider integration.
  headers.delete(CSRF_HEADER_NAME);
  headers.delete('X-XSRF-TOKEN');
  headers.delete('Cookie');

  return {
    ...requestArgs,
    credentials: 'omit',
    headers
  };
};

const createCredentiallessBaseQuery = (options = {}) => {
  const baseQuery = fetchBaseQuery({
    ...options,
    credentials: 'omit'
  });

  return (args, api, extraOptions) => baseQuery(
    requestWithoutBrowserCredentials(args),
    api,
    extraOptions
  );
};

const binaryResponseHandler = async (response) => ({
  data: await response.blob(),
  headers: Object.fromEntries(response.headers.entries())
});

export const createBffApiBaseQuery = (clientName, options = {}) => {
  assertApiClientAllowlisted(clientName, API_CLIENT_TYPES.BFF);

  const baseQuery = createBffBaseQuery({
    ...options,
    backendBaseUrl: options.backendBaseUrl ?? BACKEND_BASE_URL
  });

  return (args, api, extraOptions) => {
    if (shouldBlockAccountRequest(api.getState(), clientName, api.endpoint)) {
      return {
        error: {
          status: 'CUSTOM_ERROR',
          error: 'Account requests are disabled during logout.'
        }
      };
    }

    return baseQuery(args, api, extraOptions);
  };
};

export const createPublicApiBaseQuery = (clientName, options = {}) => {
  assertApiClientAllowlisted(clientName, API_CLIENT_TYPES.PUBLIC);
  return createCredentiallessBaseQuery(options);
};

export const createExternalApiBaseQuery = (clientName, options = {}) => {
  assertApiClientAllowlisted(clientName, API_CLIENT_TYPES.EXTERNAL);
  return createCredentiallessBaseQuery(options);
};

export const createPublicBinaryApiBaseQuery = (clientName, options = {}) => {
  const baseQuery = createPublicApiBaseQuery(clientName, options);

  return (args, api, extraOptions) => {
    const requestArgs = typeof args === 'string' ? { url: args } : { ...args };
    return baseQuery(
      { ...requestArgs, responseHandler: binaryResponseHandler },
      api,
      extraOptions
    );
  };
};
